/**
 * Session Debt Model — unified fairness, pacing, and variety signals.
 *
 * Uses courtsObserved (event-based wait) relative to peers at the same game tier,
 * plus recent appearance load from match history.
 */

import { MATCHMAKING_CONFIG } from './matchmakingConfig.js';
import { getUnitPlayers } from './unitBuilder.js';
import { marginalVarietyGainForUnit } from './sessionVarietyMetrics.js';

const DEFAULT_SESSION_DEBT_CONFIG = {
  recentWindowGames: 8,
  courtsPerGameAhead: 1.5,
  waitGuaranteeMultiplier: 1.5,
  minDistinctQuartets: 3,
  weights: {
    relative: 1.0,
    urgency: 0.8,
    game: 0.6,
    recent: 0.5,
    variety: 0.4,
  },
};

/**
 * @returns {boolean}
 */
export function isSessionDebtModelEnabled() {
  return MATCHMAKING_CONFIG.featureFlags?.sessionDebtModel !== false;
}

/**
 * @returns {Object}
 */
export function getSessionDebtConfig() {
  return {
    ...DEFAULT_SESSION_DEBT_CONFIG,
    ...(MATCHMAKING_CONFIG.sessionDebt || {}),
    weights: {
      ...DEFAULT_SESSION_DEBT_CONFIG.weights,
      ...(MATCHMAKING_CONFIG.sessionDebt?.weights || {}),
    },
  };
}

/**
 * Unified wait guarantee threshold: ceil(numberOfCourts × multiplier).
 * @param {number} [numberOfCourts]
 * @returns {number}
 */
export function computeWaitGuaranteeThreshold(numberOfCourts) {
  const config = getSessionDebtConfig();
  const courts = numberOfCourts ?? MATCHMAKING_CONFIG.waitGuarantee?.courtsObservedThreshold ?? 4;
  if (!isSessionDebtModelEnabled()) {
    return courts;
  }
  return Math.ceil(courts * config.waitGuaranteeMultiplier);
}

function getPlayerEffectiveGames(playerId, playersById) {
  const player = playersById[playerId];
  if (!player) return 0;
  return player.effectiveGamesPlayed ?? player.gamesPlayedThisSession ?? 0;
}

function getCourtsObserved(playerId, playerWaitMetrics) {
  return playerWaitMetrics?.[playerId]?.courtsObserved ?? 0;
}

function median(values) {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2;
  }
  return sorted[mid];
}

/**
 * Median courtsObserved per effectiveGames tier among waitlist candidates.
 * @param {string[]} waitlistPlayerIds
 * @param {Object} playersById
 * @param {Object} playerWaitMetrics
 * @returns {Map<number, number>}
 */
export function computeTierMedians(waitlistPlayerIds, playersById, playerWaitMetrics) {
  const byTier = new Map();
  for (const playerId of waitlistPlayerIds) {
    const tier = getPlayerEffectiveGames(playerId, playersById);
    const courts = getCourtsObserved(playerId, playerWaitMetrics);
    if (!byTier.has(tier)) byTier.set(tier, []);
    byTier.get(tier).push(courts);
  }
  const medians = new Map();
  for (const [tier, courtsList] of byTier) {
    medians.set(tier, median(courtsList));
  }
  return medians;
}

/**
 * Count appearances in the last K doubles games from match history.
 * @param {string} playerId
 * @param {Object} sessionState
 * @param {number} windowSize
 * @returns {number} 0–1 load ratio
 */
export function computeRecentLoadDebt(playerId, sessionState, windowSize) {
  const history = sessionState?.matchHistory || sessionState?.match_history || [];
  if (!history.length || windowSize <= 0) return 0;

  const recent = history.slice(-windowSize);
  let appearances = 0;
  for (const game of recent) {
    const t1 = game.team1 || [];
    const t2 = game.team2 || [];
    const ids = [...t1, ...t2].map((e) => (typeof e === 'string' ? e : e?.id)).filter(Boolean);
    if (ids.includes(playerId)) appearances += 1;
  }
  return appearances / recent.length;
}

/**
 * @param {string} playerId
 * @param {Object} playersById
 * @param {Object} playerWaitMetrics
 * @param {Map<number, number>} tierMedians
 * @param {number} sessionMinGames
 * @param {Object} sessionState
 * @param {Object} [configOverride]
 * @returns {{ gameCountDebt: number, relativeWaitDebt: number, waitUrgency: number, recentLoadDebt: number, priority: number, courtsObserved: number }}
 */
export function computePlayerDebts(
  playerId,
  playersById,
  playerWaitMetrics,
  tierMedians,
  sessionMinGames,
  sessionState,
  configOverride = null,
) {
  const config = configOverride || getSessionDebtConfig();
  const weights = config.weights;

  const effectiveGames = getPlayerEffectiveGames(playerId, playersById);
  const courtsObserved = getCourtsObserved(playerId, playerWaitMetrics);
  const gameCountDebt = Math.max(0, effectiveGames - (sessionMinGames ?? 0));
  const tierMedian = tierMedians.get(effectiveGames) ?? courtsObserved;
  const relativeWaitDebt = courtsObserved - tierMedian;
  const expectedCourtsForTier = gameCountDebt * config.courtsPerGameAhead;
  const waitUrgency = courtsObserved - expectedCourtsForTier;
  const recentLoadDebt = computeRecentLoadDebt(playerId, sessionState, config.recentWindowGames);

  const priority =
    relativeWaitDebt * weights.relative
    + Math.max(0, waitUrgency) * weights.urgency
    - gameCountDebt * weights.game
    - recentLoadDebt * weights.recent;

  return {
    gameCountDebt,
    relativeWaitDebt,
    waitUrgency,
    recentLoadDebt,
    priority,
    courtsObserved,
    effectiveGames,
  };
}

/**
 * Conservative min priority across partnership members.
 * @param {Object} unit
 * @param {Object} playersById
 * @param {Object} playerWaitMetrics
 * @param {Map<number, number>} tierMedians
 * @param {number} sessionMinGames
 * @param {Object} sessionState
 * @returns {Object}
 */
export function computeUnitDebts(
  unit,
  playersById,
  playerWaitMetrics,
  tierMedians,
  sessionMinGames,
  sessionState,
) {
  const playerIds = getUnitPlayers(unit);
  const debts = playerIds.map((id) =>
    computePlayerDebts(id, playersById, playerWaitMetrics, tierMedians, sessionMinGames, sessionState),
  );
  if (debts.length === 0) {
    return { priority: 0, relativeWaitDebt: 0, gameCountDebt: 0, waitUrgency: 0, recentLoadDebt: 0 };
  }
  return {
    priority: Math.min(...debts.map((d) => d.priority)),
    relativeWaitDebt: Math.min(...debts.map((d) => d.relativeWaitDebt)),
    gameCountDebt: Math.max(...debts.map((d) => d.gameCountDebt)),
    waitUrgency: Math.min(...debts.map((d) => d.waitUrgency)),
    recentLoadDebt: Math.max(...debts.map((d) => d.recentLoadDebt)),
    playerDebts: debts,
  };
}

/**
 * Build debt map for all waitlist players.
 * @param {string[]} waitlistPlayerIds
 * @param {Object} playersById
 * @param {Object} playerWaitMetrics
 * @param {number} sessionMinGames
 * @param {Object} sessionState
 * @returns {{ tierMedians: Map, byPlayerId: Map }}
 */
export function computeSessionDebts(
  waitlistPlayerIds,
  playersById,
  playerWaitMetrics,
  sessionMinGames,
  sessionState,
) {
  const tierMedians = computeTierMedians(waitlistPlayerIds, playersById, playerWaitMetrics);
  const byPlayerId = new Map();
  for (const playerId of waitlistPlayerIds) {
    byPlayerId.set(
      playerId,
      computePlayerDebts(
        playerId,
        playersById,
        playerWaitMetrics,
        tierMedians,
        sessionMinGames,
        sessionState,
      ),
    );
  }
  return { tierMedians, byPlayerId };
}

function normalizeId(entry) {
  if (typeof entry === 'string') return entry;
  if (entry && typeof entry === 'object' && entry.id) return String(entry.id);
  return null;
}

function extractQuartetKey(game) {
  const t1 = game.team1 || [];
  const t2 = game.team2 || [];
  const ids = [...t1, ...t2].map(normalizeId).filter(Boolean);
  if (ids.length !== 4) return null;
  return ids.sort().join('-');
}

/**
 * Get personal last doubles quartet for a player from match history.
 * @param {string} playerId
 * @param {Object} sessionState
 * @returns {string|null}
 */
function getPersonalLastDoublesQuartet(playerId, sessionState) {
  const history = sessionState?.matchHistory || sessionState?.match_history || [];
  for (let i = history.length - 1; i >= 0; i -= 1) {
    const game = history[i];
    const t1 = game.team1 || [];
    const t2 = game.team2 || [];
    const ids = [...t1, ...t2].map(normalizeId).filter(Boolean);
    if (ids.length === 4 && ids.includes(playerId)) {
      return ids.sort().join('-');
    }
  }
  return null;
}

/**
 * Quartet novelty for a proposed combo: 1 = all new together, 0 = exact repeat quartet.
 * @param {string[]} playerIds - 4 player ids
 * @param {Object} sessionState
 * @returns {number}
 */
export function computeQuartetNovelty(playerIds, sessionState) {
  if (!playerIds || playerIds.length !== 4) return 0;
  const proposedKey = [...playerIds].sort().join('-');
  const history = sessionState?.matchHistory || sessionState?.match_history || [];
  for (const game of history) {
    const key = extractQuartetKey(game);
    if (key === proposedKey) return 0;
  }
  return 1;
}

/**
 * Combo-level variety debt score (0–100, higher = more novel).
 * @param {Object[]} team1Players
 * @param {Object[]} team2Players
 * @param {Object} sessionState
 * @param {Array} partnerships
 * @returns {number}
 */
export function computeComboVarietyDebt(team1Players, team2Players, sessionState, partnerships = []) {
  const allIds = [
    ...team1Players.map((p) => p.id),
    ...team2Players.map((p) => p.id),
  ].filter(Boolean);
  if (allIds.length !== 4) return 50;

  const quartetNovelty = computeQuartetNovelty(allIds, sessionState);
  const varietyGain = marginalVarietyGainForUnit(
    allIds,
    allIds,
    Object.keys(sessionState?.gamesPlayedThisSession || {}),
    sessionState,
    partnerships,
  );

  return Math.min(100, quartetNovelty * 60 + varietyGain * 40);
}

/**
 * Whether min-tier early return should be skipped for pool expansion.
 * @param {Object} params
 * @returns {boolean}
 */
export function shouldExpandPoolBeyondMinTier({
  unitsAtMin,
  safetyNetFilteredUnits,
  sessionMinGames,
  playersById,
  playerWaitMetrics,
  sessionState,
  playersPerMatch = 4,
  ppm = 4,
}) {
  if (!isSessionDebtModelEnabled()) return false;

  const config = getSessionDebtConfig();
  const minTierPlayerIds = new Set(
    unitsAtMin.flatMap((u) => getUnitPlayers(u)),
  );
  const beyondMinCount = safetyNetFilteredUnits.filter((unit) => {
    const avg = getUnitPlayers(unit).reduce(
      (sum, id) => sum + getPlayerEffectiveGames(id, playersById),
      0,
    ) / Math.max(1, getUnitPlayers(unit).length);
    return avg > sessionMinGames + 1e-6;
  }).flatMap((u) => getUnitPlayers(u)).length;

  if (beyondMinCount >= ppm) {
    return true;
  }

  const minTierIds = [...minTierPlayerIds];
  if (minTierIds.length >= ppm) {
    const quartets = new Set();
    if (minTierIds.length === ppm) {
      quartets.add([...minTierIds].sort().join('-'));
    } else {
      for (let i = 0; i < minTierIds.length; i += 1) {
        for (let j = i + 1; j < minTierIds.length; j += 1) {
          for (let k = j + 1; k < minTierIds.length; k += 1) {
            for (let l = k + 1; l < minTierIds.length; l += 1) {
              quartets.add([minTierIds[i], minTierIds[j], minTierIds[k], minTierIds[l]].sort().join('-'));
            }
          }
        }
      }
    }
    if (quartets.size < config.minDistinctQuartets) {
      return true;
    }

    const lastQuartets = new Set(
      minTierIds.map((id) => getPersonalLastDoublesQuartet(id, sessionState)).filter(Boolean),
    );
    if (lastQuartets.size === 1 && minTierIds.length >= ppm) {
      const onlyKey = [...lastQuartets][0];
      const minKey = [...minTierIds].sort().join('-');
      if (onlyKey === minKey || minTierIds.every((id) => {
        const q = getPersonalLastDoublesQuartet(id, sessionState);
        return q === onlyKey;
      })) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Session debt fairness boost for a proposed combo (additive to fairness dimension).
 * @param {Object[]} team1Players
 * @param {Object[]} team2Players
 * @param {Object} sessionState
 * @param {Object} playersById
 * @param {number|null} gamesMin
 * @returns {number}
 */
export function computeSessionDebtFairnessBoost(
  team1Players,
  team2Players,
  sessionState,
  playersById,
  gamesMin,
) {
  if (!isSessionDebtModelEnabled()) return 0;

  const playerWaitMetrics = sessionState?.playerWaitMetrics || {};
  const allPlayers = [...team1Players, ...team2Players];
  const waitlistIds = Object.keys(sessionState?.gamesPlayedThisSession || playersById);
  const tierMedians = computeTierMedians(waitlistIds, playersById, playerWaitMetrics);

  let boost = 0;
  const config = getSessionDebtConfig();

  for (const player of allPlayers) {
    const debts = computePlayerDebts(
      player.id,
      playersById,
      playerWaitMetrics,
      tierMedians,
      gamesMin,
      sessionState,
    );
    boost += debts.relativeWaitDebt * 3;
    if (debts.waitUrgency > 0) {
      boost += Math.min(15, debts.waitUrgency * 2);
    }
    boost -= debts.recentLoadDebt * config.weights.recent * 10;
  }

  return boost;
}

/**
 * Whether strict pass found novel alternatives — skip exactRepeat relaxation.
 * @param {Object|null} strictPassResult - result from level 0 pass
 * @param {Object} sessionState
 * @returns {boolean}
 */
export function hasStrictNovelAlternatives(strictPassResult, sessionState) {
  if (!isSessionDebtModelEnabled() || !strictPassResult?.result) return false;

  const teams = strictPassResult.result;
  const team1 = teams.team1 || [];
  const team2 = teams.team2 || [];
  const ids = [...team1, ...team2];
  if (ids.length !== 4) return false;

  return computeQuartetNovelty(ids, sessionState) > 0;
}

/**
 * Sort pool units by session debt priority (descending).
 * @param {Object[]} poolUnits
 * @param {Object} playersById
 * @param {Object} playerWaitMetrics
 * @param {number} sessionMinGames
 * @param {Object} sessionState
 * @param {string[]} waitlistPlayerIds
 * @returns {Object[]}
 */
export function sortPoolUnitsBySessionDebt(
  poolUnits,
  playersById,
  playerWaitMetrics,
  sessionMinGames,
  sessionState,
  waitlistPlayerIds,
) {
  if (!isSessionDebtModelEnabled() || !poolUnits?.length) return poolUnits;

  const tierMedians = computeTierMedians(waitlistPlayerIds, playersById, playerWaitMetrics);
  return [...poolUnits].sort((unitA, unitB) => {
    const scoreA = computeUnitCompositeScore(
      unitA,
      playersById,
      playerWaitMetrics,
      tierMedians,
      sessionMinGames,
      sessionState,
      [],
      0,
    );
    const scoreB = computeUnitCompositeScore(
      unitB,
      playersById,
      playerWaitMetrics,
      tierMedians,
      sessionMinGames,
      sessionState,
      [],
      0,
    );
    return scoreB - scoreA;
  });
}

/**
 * Unit composite score for pool ranking (session debt + variety).
 * @param {Object} unit
 * @param {Object} playersById
 * @param {Object} playerWaitMetrics
 * @param {Map<number, number>} tierMedians
 * @param {number} sessionMinGames
 * @param {Object} sessionState
 * @param {Array} partnerships
 * @param {number} varietyScore
 * @returns {number}
 */
export function computeUnitCompositeScore(
  unit,
  playersById,
  playerWaitMetrics,
  tierMedians,
  sessionMinGames,
  sessionState,
  partnerships,
  varietyScore = 0,
) {
  if (!isSessionDebtModelEnabled()) {
    const unitGames = getUnitPlayers(unit).reduce(
      (sum, id) => sum + getPlayerEffectiveGames(id, playersById),
      0,
    ) / Math.max(1, getUnitPlayers(unit).length);
    const gap = unitGames - sessionMinGames;
    if (gap <= 0) return 1.0;
    if (gap <= 1) return 0.7;
    return 0.4;
  }

  const debts = computeUnitDebts(
    unit,
    playersById,
    playerWaitMetrics,
    tierMedians,
    sessionMinGames,
    sessionState,
  );
  const config = getSessionDebtConfig();
  const w = config.weights;

  const normalizedPriority = Math.max(0, Math.min(1, (debts.priority + 5) / 10));
  const gamePenalty = Math.max(0, Math.min(1, debts.gameCountDebt * 0.3));

  return (
    normalizedPriority * 0.35
    + Math.max(0, debts.relativeWaitDebt * 0.05) * 0.25
    + Math.max(0, debts.waitUrgency * 0.05) * 0.25
    - gamePenalty * 0.2
    - debts.recentLoadDebt * 0.1
    + varietyScore * w.variety * 0.1
  );
}
